import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "../../contexts/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { isIOSNative } from "../../lib/platform";
import api from "../../lib/api";

/**
 * AppleSignInButton — uses Apple's OFFICIAL renderButton API.
 *
 * Apple's SDK renders an HIG-compliant button into the host <div>. We do not
 * apply any custom styling on top of the rendered button, so Apple can never
 * reject for design issues (Guideline 4.0). This addresses the iPad-cropped-logo
 * rejection that previous inline-SVG and inline-IMG approaches kept hitting.
 *
 * Auth flow:
 *   • Web (and any non-Capacitor WebView):
 *       Apple's renderButton handles click → triggers AppleID.auth.signIn() popup
 *       → success event posts id_token to /api/auth/apple.
 *   • iOS native (Capacitor):
 *       Capture-phase click handler intercepts the rendered button BEFORE
 *       Apple's handler runs, then calls the Capacitor plugin
 *       (window.Capacitor.Plugins.SignInWithApple) which uses the native
 *       ASAuthorizationController flow under the hood.
 *
 * IDs:
 *   APPLE_SERVICE_ID = "com.thqah.signin" (web)
 *   APPLE_BUNDLE_ID  = "com.thqah.app"   (iOS native)
 */

const APPLE_SERVICE_ID = "com.thqah.signin";
const APPLE_BUNDLE_ID = "com.thqah.app";
const APPLE_REDIRECT_URI =
  typeof window !== "undefined"
    ? `${window.location.origin}/api/auth/apple/callback`
    : "/api/auth/apple/callback";
const APPLE_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

let _sdkPromise = null;
function loadAppleSDK() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.AppleID?.auth) return Promise.resolve(window.AppleID);
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = APPLE_SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.AppleID);
    s.onerror = () => reject(new Error("Failed to load Apple SDK"));
    document.head.appendChild(s);
  });
  return _sdkPromise;
}

function genNonce(len = 32) {
  const bytes = new Uint8Array(len);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function AppleSignInButton() {
  const { lang } = useI18n();
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const ar = lang === "ar";
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const isNative = isIOSNative();

  const finishWithIdentityToken = useCallback(
    async (identity_token, full_name) => {
      const refCode =
        (typeof window !== "undefined" && window.localStorage.getItem("ref_code")) || null;
      const { data } = await api.post("/auth/apple", {
        identity_token,
        full_name: full_name || null,
        referral_code: refCode || undefined,
      });
      const u = loginWithToken({ token: data.token, user: data.user });
      toast.success(ar ? `أهلاً ${u.full_name || ""}` : `Welcome ${u.full_name || ""}`);

      const needsOnboarding =
        u.is_new_user === true || !u.onboarding_complete;

      const dest = needsOnboarding
        ? "/onboarding"
        : u.role === "admin"
          ? "/admin"
          : u.role === "expert"
            ? "/expert"
            : "/dashboard";
      nav(dest, { replace: true });
    },
    [ar, loginWithToken, nav],
  );

  // ── iOS native flow (Capacitor plugin) ──────────────────────────────
  const signInNative = useCallback(async () => {
    try {
      const plugin = window?.Capacitor?.Plugins?.SignInWithApple;
      if (!plugin?.authorize) {
        toast.error(ar ? "غير متاح على هذا الجهاز" : "Not available on this device");
        return;
      }
      const result = await plugin.authorize({
        clientId: APPLE_BUNDLE_ID,
        redirectURI: APPLE_REDIRECT_URI,
        scopes: "email name",
        state: genNonce(8),
        nonce: genNonce(16),
      });
      const id = result?.response?.identityToken;
      if (!id) throw new Error("No identityToken");
      const fullName =
        [result?.response?.givenName, result?.response?.familyName]
          .filter(Boolean)
          .join(" ") || null;
      await finishWithIdentityToken(id, fullName);
    } catch (e) {
      console.error("Apple native sign in failed", e);
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        (ar ? "فشل تسجيل الدخول" : "Sign-in failed");
      if (!/cancel|cancelled|1001/i.test(String(e?.message || ""))) toast.error(msg);
    }
  }, [ar, finishWithIdentityToken]);

  // ── Initialise SDK + render official Apple button ───────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const AppleID = await loadAppleSDK();
        if (!mounted || !AppleID?.auth) return;
        AppleID.auth.init({
          clientId: APPLE_SERVICE_ID,
          scope: "email name",
          redirectURI: APPLE_REDIRECT_URI,
          state: genNonce(8),
          nonce: genNonce(16),
          usePopup: true,
        });
        // Render the OFFICIAL Apple-styled button into our container.
        // Apple's SDK reads `data-*` attributes from the element to style it.
        if (containerRef.current && AppleID.auth.renderButton) {
          try {
            AppleID.auth.renderButton(containerRef.current);
          } catch (e) {
            // Older SDK signatures expect no argument
            try { AppleID.auth.renderButton(); } catch { /* noop */ }
          }
        }
        setReady(true);
      } catch (e) {
        console.warn("Apple SDK init failed:", e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Listen for Apple's success/failure events on web ────────────────
  useEffect(() => {
    const onSuccess = async (ev) => {
      const detail = ev?.detail || {};
      const id = detail.authorization?.id_token;
      if (!id) return;
      const userInfo = detail.user;
      const fullName = userInfo?.name
        ? [userInfo.name.firstName, userInfo.name.lastName].filter(Boolean).join(" ")
        : null;
      try {
        await finishWithIdentityToken(id, fullName);
      } catch (e) {
        toast.error(
          e?.response?.data?.detail ||
            (ar ? "فشل تسجيل الدخول" : "Sign-in failed"),
        );
      }
    };
    const onFailure = (ev) => {
      const err = ev?.detail?.error;
      if (err && !/popup_closed|user_cancelled_authorize/i.test(String(err))) {
        toast.error(ar ? "فشل تسجيل الدخول" : "Sign-in failed");
      }
    };
    document.addEventListener("AppleIDSignInOnSuccess", onSuccess);
    document.addEventListener("AppleIDSignInOnFailure", onFailure);
    return () => {
      document.removeEventListener("AppleIDSignInOnSuccess", onSuccess);
      document.removeEventListener("AppleIDSignInOnFailure", onFailure);
    };
  }, [ar, finishWithIdentityToken]);

  // ── On iOS native: intercept the rendered button click ──────────────
  // Apple's renderButton wires its own click handler that tries the web
  // popup flow. In a Capacitor WebView that path is blocked, so we capture
  // the click before Apple's handler and route to the native plugin.
  useEffect(() => {
    if (!isNative || !containerRef.current) return;
    const el = containerRef.current;
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      signInNative();
    };
    el.addEventListener("click", handler, true /* capture */);
    return () => el.removeEventListener("click", handler, true);
  }, [isNative, signInNative, ready]);

  return (
    <div
      ref={containerRef}
      id="appleid-signin"
      className="w-full ios-tap"
      // HIG-compliant attributes consumed by Apple's renderButton:
      data-color="black"
      data-border="true"
      data-type={ar ? "continue" : "sign-in"}
      data-mode="center-align"
      data-logo-size="medium"
      data-label-position="0"
      data-border-radius="12"
      data-width="320"
      data-height="50"
      data-locale={ar ? "ar_SA" : "en_US"}
      data-testid="login-apple"
      aria-label={ar ? "تسجيل الدخول بـ Apple" : "Sign in with Apple"}
    />
  );
}
